import { useCallback, useMemo, useState } from "react";

import type {
  AlertConfigurationsDto,
  KeywordAlertConfigurationCreateRequest,
  KeywordAlertConfigurationDto,
  ThresholdAlertConfigurationCreateRequest,
  ThresholdAlertConfigurationDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Radio } from "@/design-system/Radio";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type { AlertDetailFormValues } from "./components/AlertDetailsModal";
import { AlertDetailsModal } from "./components/AlertDetailsModal";

export interface AlertSettingsProps {
  alerts: AlertConfigurationsDto;
  isLoading: boolean;
  createLikeAlert: (payload: ThresholdAlertConfigurationCreateRequest) => Promise<void>;
  createCommentAlert: (payload: ThresholdAlertConfigurationCreateRequest) => Promise<void>;
  createPostKeywordsAlert: (payload: KeywordAlertConfigurationCreateRequest) => Promise<void>;
  createCommentKeywordsAlert: (payload: KeywordAlertConfigurationCreateRequest) => Promise<void>;
  editLikeAlert: ({ id, payload }: { id: string; payload: ThresholdAlertConfigurationCreateRequest }) => Promise<void>;
  editCommentAlert: ({
    id,
    payload,
  }: {
    id: string;
    payload: ThresholdAlertConfigurationCreateRequest;
  }) => Promise<void>;
  editPostKeywordsAlert: ({
    id,
    payload,
  }: {
    id: string;
    payload: KeywordAlertConfigurationCreateRequest;
  }) => Promise<void>;
  editCommentKeywordsAlert: ({
    id,
    payload,
  }: {
    id: string;
    payload: KeywordAlertConfigurationCreateRequest;
  }) => Promise<void>;
}

export interface SaveRequest {
  enabled: boolean;
  name: string;
  email: string;
  projectIds: string[];
  threshold?: number;
  keywords?: string[];
  enableEmail?: boolean;
}

export type AlertType = "reactions" | "comments" | "keywords_post" | "keywords_comments";

export function AlertSettings({
  alerts,
  isLoading,
  createLikeAlert,
  createCommentAlert,
  createPostKeywordsAlert,
  createCommentKeywordsAlert,
  editCommentAlert,
  editLikeAlert,
  editPostKeywordsAlert,
  editCommentKeywordsAlert,
}: AlertSettingsProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const defaultFormValues = {
    id: "",
    type: "reactions",
    enabled: true,
    emailEnabled: true,
    name: "",
    email: sessionUser.email,
    projects: [],
    threshold: "0",
    keywords: "",
  } satisfies AlertDetailFormValues;

  const { t } = useTranslation();
  const [isModalOpened, modalOpenHandlers] = useBool(false);
  const [alertValues, setAlertValues] = useState<AlertDetailFormValues>(defaultFormValues);

  const onSaveAlert = useCallback(
    async ({ id, type, payload }: { id: string; type: AlertType; payload: SaveRequest }) => {
      const alertNotificationType = payload.enableEmail ? "dashboardAndEmail" : "dashboardOnly";
      if (type === "reactions") {
        if (id) {
          await editLikeAlert({
            id,
            payload: {
              enabled: payload.enabled,
              name: payload.name,
              email: payload.email,
              projectIds: payload.projectIds,
              percentageThreshold: payload.threshold!,
              alertNotificationType,
            },
          });
        } else {
          await createLikeAlert({
            enabled: payload.enabled,
            name: payload.name,
            email: payload.email,
            projectIds: payload.projectIds,
            percentageThreshold: payload.threshold!,
            alertNotificationType,
          });
        }
      } else if (type === "comments") {
        if (id) {
          await editCommentAlert({
            id,
            payload: {
              enabled: payload.enabled,
              name: payload.name,
              email: payload.email,
              projectIds: payload.projectIds,
              percentageThreshold: payload.threshold!,
              alertNotificationType,
            },
          });
        } else {
          await createCommentAlert({
            enabled: payload.enabled,
            name: payload.name,
            email: payload.email,
            projectIds: payload.projectIds,
            percentageThreshold: payload.threshold!,
            alertNotificationType,
          });
        }
      } else if (type === "keywords_post") {
        if (id) {
          await editPostKeywordsAlert({
            id,
            payload: {
              enabled: payload.enabled,
              name: payload.name,
              email: payload.email,
              projectIds: payload.projectIds,
              keywords: payload.keywords!,
              alertNotificationType,
            },
          });
        } else {
          await createPostKeywordsAlert({
            enabled: payload.enabled,
            name: payload.name,
            email: payload.email,
            projectIds: payload.projectIds,
            keywords: payload.keywords!,
            alertNotificationType,
          });
        }
      } else if (type === "keywords_comments") {
        if (id) {
          await editCommentKeywordsAlert({
            id,
            payload: {
              enabled: payload.enabled,
              name: payload.name,
              email: payload.email,
              projectIds: payload.projectIds,
              keywords: payload.keywords!,
              alertNotificationType,
            },
          });
        } else {
          await createCommentKeywordsAlert({
            enabled: payload.enabled,
            name: payload.name,
            email: payload.email,
            projectIds: payload.projectIds,
            keywords: payload.keywords!,
            alertNotificationType,
          });
        }
      }
    },
    [
      editLikeAlert,
      createLikeAlert,
      createPostKeywordsAlert,
      createCommentKeywordsAlert,
      editCommentAlert,
      createCommentAlert,
      editPostKeywordsAlert,
      editCommentKeywordsAlert,
    ],
  );

  const likesAndCommentsColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<ThresholdAlertConfigurationDto>();

    return [
      helper.accessor("name", {
        header: t("page.alerts.table.type"),
        cell: (cell) => cell.getValue(),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("projects", {
        header: t("page.alerts.table.projects"),
        cell: (cell) => (
          <PillList
            items={cell.getValue().map((project) => ({
              label: project.name,
            }))}
            maxShown={3}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.TAGS.MIN,
        size: TABLE_COLUMN_SIZES.TAGS.DEFAULT,
      }),
      helper.accessor("enabled", {
        header: t("page.alerts.table.state"),
        cell: (cell) => (
          <StateToggle
            rowId={cell.row.id}
            enabled={cell.getValue()}
            emailEnabled={cell.row.original.alertNotificationType === "dashboardAndEmail"}
            onChange={(enabled, enableEmail) =>
              onSaveAlert({
                id: cell.row.original.id,
                type: cell.row.id.includes("n_of_likes_alert") ? "reactions" : "comments",
                payload: {
                  enabled,
                  enableEmail,
                  name: cell.row.original.name,
                  email: cell.row.original.email,
                  projectIds: cell.row.original.projects.map((project) => project.id),
                  threshold: cell.row.original.percentageThreshold,
                },
              })
            }
          />
        ),
        size: 540,
        enableResizing: false,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("page.alerts.table.actions.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                setAlertValues({
                  id: cell.row.original.id,
                  type: cell.row.id.includes("n_of_likes_alert") ? "reactions" : "comments",
                  enabled: cell.row.original.enabled,
                  name: cell.row.original.name,
                  projects: cell.row.original.projects,
                  email: cell.row.original.email,
                  threshold: cell.row.original.percentageThreshold?.toString(),
                  emailEnabled: cell.row.original.alertNotificationType === "dashboardAndEmail",
                });
                modalOpenHandlers.setTrue();
              },
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, modalOpenHandlers, onSaveAlert]);

  const keywordsColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<KeywordAlertConfigurationDto>();

    return [
      helper.accessor("name", {
        header: t("page.alerts.table.type"),
        cell: (cell) => cell.getValue(),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("projects", {
        header: t("page.alerts.table.projects"),
        cell: (cell) => (
          <PillList
            items={cell.getValue().map((project) => ({
              label: project.name,
            }))}
            maxShown={3}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.TAGS.MIN,
        size: TABLE_COLUMN_SIZES.TAGS.DEFAULT,
      }),
      helper.accessor("enabled", {
        header: t("page.alerts.table.state"),
        cell: (cell) => (
          <StateToggle
            rowId={cell.row.id}
            enabled={cell.getValue()}
            emailEnabled={cell.row.original.alertNotificationType === "dashboardAndEmail"}
            onChange={(enabled, enableEmail) =>
              onSaveAlert({
                id: cell.row.original.id,
                type: cell.row.id.includes("post") ? "keywords_post" : "keywords_comments",
                payload: {
                  enabled,
                  enableEmail,
                  name: cell.row.original.name,
                  email: cell.row.original.email,
                  projectIds: cell.row.original.projects.map((project) => project.id),
                  keywords: cell.row.original.keywords?.split(","),
                },
              })
            }
          />
        ),
        size: 540,
        enableResizing: false,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("page.alerts.table.actions.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                setAlertValues({
                  id: cell.row.original.id,
                  type: cell.row.id.includes("post") ? "keywords_post" : "keywords_comments",
                  enabled: cell.row.original.enabled,
                  name: cell.row.original.name,
                  email: cell.row.original.email,
                  projects: cell.row.original.projects,
                  keywords: cell.row.original.keywords,
                  emailEnabled: cell.row.original.alertNotificationType === "dashboardAndEmail",
                });
                modalOpenHandlers.setTrue();
              },
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, modalOpenHandlers, onSaveAlert]);

  const numberOfLikesTableInstance = useResizableTable(TABLE_KEYS.ALERT_LIKES_THRESHOLD, {
    columns: likesAndCommentsColumns,
    data: alerts.numberOfLikesAlerts,
    getRowId: (row) => `n_of_likes_alert_${row.id}`,
    enableSorting: false,
  });

  const numberOfCommentsTableInstance = useResizableTable(TABLE_KEYS.ALERT_COMMENTS_THRESHOLD, {
    columns: likesAndCommentsColumns,
    data: alerts.numberOfCommentsAlerts,
    getRowId: (row) => `n_of_comments_alert_${row.id}`,
    enableSorting: false,
  });

  const postKeywordsTableInstance = useResizableTable(TABLE_KEYS.ALERT_POST_KEYWORDS, {
    columns: keywordsColumns,
    data: alerts.postKeywordAlerts,
    getRowId: (row) => `post_keywords_alert_${row.id}`,
    enableSorting: false,
  });

  const commentKeywordsTableInstance = useResizableTable(TABLE_KEYS.ALERT_COMMENT_KEYWORDS, {
    columns: keywordsColumns,
    data: alerts.commentKeywordAlerts,
    getRowId: (row) => `comment_keywords_alert_${row.id}`,
    enableSorting: false,
  });

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("page.alerts.actions.n-of-reactions"),
      },
      value: "n-of-reactions",
      icon: <Edit05Icon />,
      callbackFn: () => {
        setAlertValues({ ...alertValues, type: "reactions" });
        modalOpenHandlers.setTrue();
      },
    },
    {
      labels: {
        default: t("page.alerts.actions.n-of-comments"),
      },
      value: "n-of-comments",
      icon: <Edit05Icon />,
      callbackFn: () => {
        setAlertValues({ ...alertValues, type: "comments" });
        modalOpenHandlers.setTrue();
      },
    },
    {
      labels: {
        default: t("page.alerts.actions.post-keywords"),
      },
      value: "post-keywords",
      icon: <Edit05Icon />,
      callbackFn: () => {
        setAlertValues({ ...alertValues, type: "keywords_post" });
        modalOpenHandlers.setTrue();
      },
    },
    {
      labels: {
        default: t("page.alerts.actions.comment-keywords"),
      },
      value: "comment-keywords",
      icon: <Edit05Icon />,
      callbackFn: () => {
        setAlertValues({ ...alertValues, type: "keywords_comments" });
        modalOpenHandlers.setTrue();
      },
    },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.alert-settings.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.alerts-overview.title"),
              to: routes.alerts.list(),
            },
            {
              name: t("page.alert-settings.title"),
            },
          ]}
        />
      }
      actions={<ContextMenu items={menuActions} triggerComponent={<Button>{t("page.alerts.new")}</Button>} />}
    >
      <Stack gap="lg">
        <Stack gap="sm">
          <span className="text-body-emphasis">{t("page.alerts.table.likes.title")}</span>
          {alerts.numberOfLikesAlerts.length > 0 || isLoading ? (
            <Table table={numberOfLikesTableInstance} data-testid="likes-alerts-list" isLoading={isLoading} />
          ) : (
            <span className="text-caption">{t("page.alerts.no-data")}</span>
          )}
        </Stack>
        <Stack gap="sm">
          <span className="text-body-emphasis">{t("page.alerts.table.comments.title")}</span>
          {alerts.numberOfCommentsAlerts.length > 0 || isLoading ? (
            <Table table={numberOfCommentsTableInstance} data-testid="comments-alerts-list" isLoading={isLoading} />
          ) : (
            <span className="text-caption">{t("page.alerts.no-data")}</span>
          )}
        </Stack>
        <Stack gap="sm">
          <span className="text-body-emphasis">{t("page.alerts.table.post-keywords.title")}</span>
          {alerts.postKeywordAlerts.length > 0 || isLoading ? (
            <Table table={postKeywordsTableInstance} data-testid="post-keywords-alerts-list" isLoading={isLoading} />
          ) : (
            <span className="text-caption">{t("page.alerts.no-data")}</span>
          )}
        </Stack>
        <Stack gap="sm">
          <span className="text-body-emphasis">{t("page.alerts.table.comment-keywords.title")}</span>
          {alerts.commentKeywordAlerts.length > 0 || isLoading ? (
            <Table
              table={commentKeywordsTableInstance}
              data-testid="comment-keywords-alerts-list"
              isLoading={isLoading}
            />
          ) : (
            <span className="text-caption">{t("page.alerts.no-data")}</span>
          )}
        </Stack>
      </Stack>
      <AlertDetailsModal
        alert={alertValues}
        onSave={onSaveAlert}
        isOpened={isModalOpened}
        onOpenChange={modalOpenHandlers.set}
      />
    </DocumentPage>
  );
}

function StateToggle({
  rowId,
  enabled,
  emailEnabled,
  onChange,
}: {
  rowId: string;
  enabled: boolean;
  emailEnabled: boolean;
  onChange: (enabled: boolean, email: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <Inline gap="lg" wrap={false}>
      <label className="inline-flex flex-row-reverse items-center justify-end gap-space-sm">
        <span className="whitespace-nowrap">{t("page.alerts.table.state.email-and-dashboard")}</span>
        <Radio
          value={`${rowId}-email-and-dashboard`}
          name={`${rowId}_email_and_dashboard`}
          checked={enabled && emailEnabled}
          onChange={() => onChange(true, true)}
        />
      </label>
      <label className="inline-flex flex-row-reverse items-center justify-end gap-space-sm">
        <span className="whitespace-nowrap">{t("page.alerts.table.state.dashboard")}</span>
        <Radio
          value={`${rowId}-dashboard`}
          name={`${rowId}_dashboard`}
          checked={enabled && !emailEnabled}
          onChange={() => onChange(true, false)}
        />
      </label>
      <label className="inline-flex flex-row-reverse items-center justify-end gap-space-sm">
        <span className="whitespace-nowrap">{t("page.alerts.table.state.off")}</span>
        <Radio
          value={`${rowId}_disabled`}
          name={`${rowId}_disabled`}
          checked={!enabled}
          onChange={() => onChange(false, false)}
        />
      </label>
    </Inline>
  );
}
